import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const navigate = useNavigate();
  const gridColor = 'rgba(0,0,0,0.05)';
  const tickColor = '#6b7280';
  const [stats, setStats] = useState({
    activeProjectsCount: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    outstandingAmount: 0,
    profitMargin: 0,
    pendingEnquiriesCount: 0
  });
  
  const [revenueChartData, setRevenueChartData] = useState(null);
  const [expenseChartData, setExpenseChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 5 minutes (300000 ms)
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 300000);
    
    // Cleanup interval on unmount
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [statsRes, revenueRes, expenseRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/dashboard/revenue-chart'),
        axios.get('/dashboard/expense-chart')
      ]);
      
      setStats(statsRes.data);
      setRevenueChartData(prepareRevenueChartData(revenueRes.data));
      setExpenseChartData(prepareExpenseChartData(expenseRes.data));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const prepareRevenueChartData = (data) => {
    if (!data || !data.labels || !data.datasets) return null;
    return {
      labels: data.labels,
      datasets: data.datasets.map(dataset => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: '#e8590c',
        backgroundColor: 'rgba(232,89,12,0.1)',
        pointBackgroundColor: '#e8590c',
        pointRadius: 4,
        tension: 0.4,
        fill: true,
      }))
    };
  };

  const prepareExpenseChartData = (data) => {
    if (!data || !data.labels || !data.datasets) return null;
    const colors = [
      '#e8590c', '#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899'
    ];
    return {
      labels: data.labels,
      datasets: data.datasets.map(dataset => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: colors,
        borderColor: '#fff',
        borderWidth: 2,
      }))
    };
  };



  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(2)}%`;
  };

  if (loading && !stats.activeProjectsCount) {
    return (
      <div className="dashboard-container">
        <h1>Dashboard</h1>
        <div className="loading">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <button onClick={fetchDashboardData} className="refresh-btn" disabled={loading}>
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card clickable" onClick={() => navigate('/projects?status=ACTIVE')}>
          <div className="kpi-icon projects">📊</div>
          <div className="kpi-content">
            <h3>Active Projects</h3>
            <p className="kpi-value">{stats.activeProjectsCount}</p>
            <small className="kpi-label">Currently in progress</small>
          </div>
        </div>

        <div className="kpi-card clickable" onClick={() => navigate('/projects?status=COMPLETED')}>
          <div className="kpi-icon revenue">💰</div>
          <div className="kpi-content">
            <h3>Total Revenue</h3>
            <p className="kpi-value">{formatCurrency(stats.totalRevenue)}</p>
            <small className="kpi-label">From completed projects</small>
          </div>
        </div>

        <div className="kpi-card clickable" onClick={() => navigate('/expenses')}>
          <div className="kpi-icon expenses">💸</div>
          <div className="kpi-content">
            <h3>Total Expenses</h3>
            <p className="kpi-value">{formatCurrency(stats.totalExpenses)}</p>
            <small className="kpi-label">Across all projects</small>
          </div>
        </div>

        <div className="kpi-card clickable" onClick={() => navigate('/vendors')}>
          <div className="kpi-icon outstanding">⚠️</div>
          <div className="kpi-content">
            <h3>Outstanding Amount</h3>
            <p className="kpi-value">{formatCurrency(stats.outstandingAmount)}</p>
            <small className="kpi-label">Pending to vendors</small>
          </div>
        </div>

        <div className="kpi-card clickable" onClick={() => navigate('/reports')}>
          <div className="kpi-icon profit">📈</div>
          <div className="kpi-content">
            <h3>Profit Margin</h3>
            <p className="kpi-value" style={{ 
              color: stats.profitMargin >= 0 ? '#27ae60' : '#e74c3c' 
            }}>
              {formatPercentage(stats.profitMargin)}
            </p>
            <small className="kpi-label">Overall profitability</small>
          </div>
        </div>

        <div className="kpi-card clickable" onClick={() => navigate('/enquiries?status=NEW,FOLLOWUP')}>
          <div className="kpi-icon enquiries">📋</div>
          <div className="kpi-content">
            <h3>Pending Enquiries</h3>
            <p className="kpi-value">{stats.pendingEnquiriesCount}</p>
            <small className="kpi-label">Awaiting follow-up</small>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        <div className="chart-card">
          <h2>Monthly Revenue Trend</h2>
          <div className="chart-container">
            {revenueChartData ? (
              <Line 
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { color: tickColor, font: { size: 11 } },
                    },
                    y: {
                      beginAtZero: true,
                      grid: { color: gridColor },
                      ticks: {
                        color: tickColor,
                        font: { size: 11 },
                         callback: (v) => getCurrencySymbol() + v.toLocaleString(sessionStorage.getItem('companyCountry') === 'India' ? 'en-IN' : 'en-US'),
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="no-data">No revenue data available</div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <h2>Expense Breakdown by Category</h2>
          <div className="chart-container">
            {expenseChartData ? (
              <Doughnut 
                data={expenseChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '65%',
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: { color: tickColor, font: { size: 11 }, boxWidth: 12 }
                    },
                    tooltip: {
                      callbacks: {
                         label: (ctx) => `${ctx.label}: ${getCurrencySymbol()}${ctx.parsed.toLocaleString(sessionStorage.getItem('companyCountry') === 'India' ? 'en-IN' : 'en-US')}`
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="no-data">No expense data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
